/**
 * Carrying an element's HTML attributes through the document.
 *
 * A block that models `<p style>` but not `<p class>` drops the class without
 * saying so, and now that a template's `<style>` block survives into the sent
 * email, the class is what the CSS in it binds to. So every block that parses
 * foreign markup stores the attributes it does not otherwise model, and puts
 * them back on the element it renders.
 */

/**
 * HTML attribute names React spells differently.
 *
 * React renders an unknown lower-case attribute as written, but warns for the
 * handful it has a camel-cased name for, and renders `class` and `for` only
 * under their JSX names. This is that handful, limited to what turns up in
 * email markup.
 */
const REACT_ATTRIBUTE_NAMES: Record<string, string> = {
  class: "className",
  for: "htmlFor",
  colspan: "colSpan",
  rowspan: "rowSpan",
  cellpadding: "cellPadding",
  cellspacing: "cellSpacing",
  valign: "vAlign",
  bgcolor: "bgColor",
  charset: "charSet",
  frameborder: "frameBorder",
  hreflang: "hrefLang",
  srcset: "srcSet",
  usemap: "useMap",
  datetime: "dateTime",
  tabindex: "tabIndex",
  maxlength: "maxLength",
  crossorigin: "crossOrigin",
  contenteditable: "contentEditable",
  spellcheck: "spellCheck",
  accesskey: "accessKey",
};

/** `style` is never included — a block stores that as its own style map. */
export function elementAttributes(
  element: HTMLElement,
  modelled: readonly string[] = [],
): Record<string, string> {
  const skip = new Set(["style", ...modelled]);
  const attributes: Record<string, string> = {};
  for (const { name, value } of Array.from(element.attributes)) {
    if (skip.has(name.toLowerCase())) continue;
    attributes[name] = value;
  }
  return attributes;
}

/** Stored attributes as React props, ready to spread onto a rendered element. */
export function storedAttributeProps(
  attrs: Record<string, unknown> | undefined,
): Record<string, string> {
  const stored = attrs?.attributes;
  if (!stored || typeof stored !== "object") return {};
  const props: Record<string, string> = {};
  for (const [name, value] of Object.entries(
    stored as Record<string, string>,
  )) {
    props[REACT_ATTRIBUTE_NAMES[name.toLowerCase()] ?? name] = value;
  }
  return props;
}
